"use client";

import { useEffect, useRef, useState } from "react";
import { Box, Heading, Spinner, Tooltip, Flex } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import echarts from "./lib";

// Days-cover buckets carry meaning left→right (nearly-out → healthy →
// over-stocked), so the bars ramp red → amber → green → blue by position.
const RAMP = [
  { t: 0, hex: "#E05248" }, // nearly out — critical
  { t: 0.3, hex: "#E8940F" }, // low
  { t: 0.65, hex: "#1AA47C" }, // healthy
  { t: 1, hex: "#3B8FD0" }, // over-stocked
];

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
};

const rampColor = (t) => {
  let lo = RAMP[0];
  let hi = RAMP[RAMP.length - 1];
  for (let i = 0; i < RAMP.length - 1; i++) {
    if (t >= RAMP[i].t && t <= RAMP[i + 1].t) {
      lo = RAMP[i];
      hi = RAMP[i + 1];
      break;
    }
  }
  const f = hi.t === lo.t ? 0 : (t - lo.t) / (hi.t - lo.t);
  const a = hexToRgb(lo.hex);
  const b = hexToRgb(hi.hex);
  const mix = a.map((c, i) => Math.round(c + (b[i] - c) * f));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
};

const Chart8 = ({ supplier, retailer }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    let disposed = false;

    const buildChart = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart8`,
          { supplier, retailer, userid: localStorage.getItem("userid") }
        );

        if (disposed) return;

        // Identical to the AmCharts version — bucket/stores used raw.
        if (!data?.data?.length) {
          setNoData(true);
          return;
        }
        setNoData(false);

        const rows = data.data;
        const totalStores = rows.reduce((a, r) => a + Number(r.stores || 0), 0);
        const n = Math.max(rows.length - 1, 1);

        if (!containerRef.current) return;
        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;
        chart.setOption({
          grid: { left: 8, right: 14, top: 26, bottom: 4, containLabel: true },
          xAxis: {
            type: "category",
            data: rows.map((r) => r.bucket),
            axisLabel: { color: "#64798A", fontSize: 11 },
            axisLine: { lineStyle: { color: "#E2E9EE" } },
            axisTick: { show: false },
          },
          yAxis: {
            type: "value",
            min: 0,
            axisLabel: { color: "#64798A", fontSize: 11 },
            splitLine: { lineStyle: { color: "rgba(229,231,235,0.5)", type: [3, 3] } },
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow", shadowStyle: { color: "rgba(26,164,124,0.07)" } },
            confine: true,
            backgroundColor: "#ffffff",
            borderColor: "#E2E9EE",
            borderWidth: 1,
            padding: [8, 12],
            textStyle: { color: "#1D2F3A", fontSize: 12 },
            extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
            formatter: (params) => {
              const p = params[0];
              const share = totalStores
                ? ((100 * p.value) / totalStores).toFixed(1)
                : "0.0";
              return `${p.name} days cover<br/><b>${p.value}</b> stores — ${share}% of all stores`;
            },
          },
          series: [
            {
              type: "bar",
              barWidth: "62%",
              data: rows.map((r, i) => {
                const base = rampColor(i / n);
                return {
                  value: r.stores,
                  itemStyle: {
                    borderRadius: [5, 5, 0, 0],
                    color: {
                      type: "linear",
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        { offset: 0, color: base },
                        { offset: 1, color: base.replace("rgb", "rgba").replace(")", ", 0.35)") },
                      ],
                    },
                  },
                };
              }),
              emphasis: {
                itemStyle: { shadowBlur: 10, shadowColor: "rgba(29,47,58,0.25)" },
              },
              label: {
                show: true,
                position: "top",
                color: "#4A5D6B",
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: "Inter, sans-serif",
              },
              labelLayout: { hideOverlap: true },
              animationDuration: 800,
              animationDelay: (idx) => idx * 50,
              animationEasing: "cubicOut",
            },
          ],
        });
      } catch (err) {
        console.error("Chart8 fetch failed", err);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    buildChart();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer]);

  return (
    <Box w="100%" h="100%" position="relative" display="flex" flexDirection="column">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">Days-Cover Distribution</Heading>
        <Tooltip label="How many stores sit in each stock-cover band. Left bars (0-7 d) = nearly out; right bars (36 + d) = over-stocked." hasArrow placement="left" maxW="240px" whiteSpace="normal">
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      <Box flex="1" position="relative">
        {noData && (
          <Flex align="center" justify="center" position="absolute" top="0" left="0" width="100%" height="100%" zIndex="1">
            <Heading size="sm" color="gray.600">
              No data available
            </Heading>
          </Flex>
        )}
        <div ref={containerRef} style={{ width: "100%", height: "100%", overflow: "hidden" }} />
        {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
      </Box>
    </Box>
  );
};

export default Chart8;
